import React, { useEffect, useState } from "react";
import { get, omitBy } from "lodash/fp";
import {
  Container,
  Pagination,
  Table,
  Footer,
  Content,
  Button,
  Form,
  Input,
  Col,
  InputGroup,
  SelectPicker,
  Checkbox,
  Header,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import SaveIcon from "@rsuite/icons/AddOutline";
import CloseIcon from "@rsuite/icons/Close";
import { CampaignFields, CampaignListParams } from "./CampaignDataTypes";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import { updateIn } from "json-immut";
import MasterEditCell from "../../components/Elements/MasterEditCell";
import ActionCell from "./ActionCell";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { useTranslation } from "react-i18next";
import { HelperServices } from "../../../core/services/HelperServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  appConstObj,
  messageTypeArr,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import "./Campaigns.css";
import { isEmptyVal } from "../../../utils/helpers";
import CheckCell from "../../components/Elements/CheckCell";
import { useSelector } from "react-redux";

function Campaigns({
  withHeader = true,
  multiSelect = true,
  showCreateForm = true,
  forComp = null,
  onSelectCampaign,
  ...props
}: {
  withHeader?: boolean;
  multiSelect?: boolean;
  showCreateForm?: boolean;
  forComp?: number;
  onSelectCampaign?: Function;
}) {
  const { Column } = Table;
  const company = useSelector((state: any) => state.company);
  const [loading, setLoading] = useState(false);
  const [campaignId, setCampaignId] = useState(0);
  const [data, setData] = useState<CampaignFields[]>([]);
  const [editData, setEditData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [page, setPage] = useState(1);
  const [sort_by, setSortBy] = useState();
  const [sort_order, setSortOrder] = useState();
  const [errors, setErrors] = useState<any>({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [openNoCampaignModal, setOpenNoCampaignModal] = useState(false);
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    status: "",
  };

  const initFormSaveData = {
    campaign_name: "",
    comment: "",
    status: 1,
  };

  const [formData, setFormData] =
    useState<CampaignListParams>(initSearchFormData);

  const [formSaveData, setFormSaveData] =
    useState<CampaignFields>(initFormSaveData);

  const [showCreate, setShowCreate] = useState<boolean>(showCreateForm);

  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    let keys: any;
    if (!checked) {
      keys = checkedKeys.filter((item) => item !== value);
    } else if (checked && multiSelect) {
      keys = [...checkedKeys, value];
    } else if (checked && !multiSelect) {
      keys = [value];
    }
    setCheckedKeys(keys);
  };

  function setSelectedCampaign() {
    if (checkedKeys.length === 0) {
      setOpenNoCampaignModal(true);
      return true;
    } else {
      const campaignData = data
        .filter((item) => item.id == checkedKeys[0])
        .shift();
      onSelectCampaign && onSelectCampaign(campaignData);
    }
  }

  async function getCampaigns(params: CampaignListParams) {
    try {
      setLoading(true);
      const res = await HelperServices.getCampaigns(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      if (!showCreateForm) {
        if (get("data.total", res) <= 0) {
          setShowCreate(UtilityServices.hasPermission("create-song"));
        } else {
          setShowCreate(false);
        }
      }

      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ name: smartSearchInput },
      ...{ company: getCompanyId() },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: CampaignListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  const handleEditData = async (id: Number) => {
    let nextData: any = {};
    let error: string = "";
    let activeItem: any = {};
    nextData = Object.assign([], data);
    activeItem = nextData?.find((item: any) => item.id === id);
    activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";

    if (activeItem.editStatus == null) {
      if (isEmptyVal(activeItem.campaign_name))
        error = t("REQUIRED", {
          field: t("CAMPAIGN").toLowerCase() + " " + t("NAME").toLowerCase(),
        });

      if (!isEmptyVal(error)) {
        UtilityServices.showToaster({
          message: error,
          type: messageTypeArr.error,
        });
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
        return;
      }

      let params = {
        campaign_name: activeItem.campaign_name,
        comment: activeItem.comment,
        status: activeItem.status,
      };

      try {
        setLoading(true);
        const response: any = await HelperServices.updateCampaign(
          activeItem.id,
          params
        );
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        setFormSaveData(formSaveData);
        getCampaigns(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );

        setLoading(false);
      } catch (error: any) {
        UtilityServices.showToaster({
          message: error?.message,
          type: messageTypeArr.error,
        });
        setLoading(false);
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
      }
    }

    setEditData(nextData);
  };

  const handleDataChanges = (id: Number, key: any, value: any) => {
    let nextData: any = {};
    nextData = Object.assign([], data);
    nextData.find((item: any) => item.id === id)[key] =
      key == "status" ? (value ? 1 : 0) : value;
    setEditData(nextData);
  };

  const clearSaveForm = () => {
    setErrors({});
    setFormSaveData(initFormSaveData);
  };

  async function addCampaign() {
    try {
      let i;
      let data = { ...formSaveData };
      let dumpError = { ...errors };
      if (
        UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
        !company.id
      ) {
        UtilityServices.showToaster({
          message: t("REQUIRED_SELECT", {
            field: t("COMPANY").toLowerCase(),
          }),
          type: messageTypeArr.error,
        });
        return false;
      }
      for (i in data) {
        if (data[i] === "" && ["campaign_name", "status"].includes(i)) {
          dumpError[i] = t("FIELD_REQUIRED");
          setErrors(dumpError);
        } else {
          delete dumpError[i];
          setErrors(dumpError);
        }
      }

      if (Object.keys(dumpError).length == 0) {
        setLoading(true);
        data.campaign_name = data.campaign_name;
        data.comment = data.comment;
        data.company_id = getCompanyId();
        const response: any = await HelperServices.createCampaign(data);
        UtilityServices.showToaster({
          message: response?.data?.message,
        });

        setPage(1);
        setFormSaveData(initFormSaveData);
        getCampaigns(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
        setLoading(false);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  const handleFormChange = (data: any) => {
    if (data.path == "status") {
      if (data.value) {
        data.value = 1;
      } else {
        data.value = 0;
      }
    }
    let form = updateIn(formSaveData, [data.path], data.value);
    setFormSaveData(form);
  };

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  const handleDataDelete = async (campaignId: number) => {
    try {
      setLoading(true);
      const response: any = await HelperServices.deleteCampaign(campaignId);
      if (response.status) {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        getCampaigns(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
      } else {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
      }
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  };

  function onPageChange(page: number) {
    setPage(page);
    getCampaigns(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCampaigns(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  const handleChangeRows = (rows: any) => {
    setPage(1);
    setRows(rows);
    getCampaigns(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const filterCampaigns = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCampaigns(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCampaigns(
      getSearchParams({
        name: "",
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  async function init() {
    try {
      setShowCreate(
        UtilityServices.hasPermission("create-song") && showCreateForm
      );
    } catch (error: any) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    if (getCompanyId() !== null) {
      getCampaigns(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (company.id) {
      getCampaigns(
        getSearchParams({
          company: company.id,
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
    setPage(1);
  }, [company]);

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (forComp) {
      compId = forComp;
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  return (
    <div
      className={`campaigns campaign ${
        withHeader ? "campaigns-page" : "campaigns-picker"
      }`}
    >
      <Container className="container">
        {withHeader && (
          <Header className="header campaign-page-header">
            <h4>{t("CAMPAIGNS")}</h4>
          </Header>
        )}
        <Content className="campaigns-content">
          {showCreate && (
            <Form formValue={formSaveData} className="entity-picker-createbar">
              <Col md={4} className="entity-picker-create-field">
                <Form.Group controlId="campaign_name">
                  <Form.Control
                    name="campaign_name"
                    size="sm"
                    placeholder={t("CAMPAIGN") + " " + t("NAME")}
                    onChange={(v: any) =>
                      handleFormChange({ path: "campaign_name", value: v })
                    }
                    value={formSaveData?.campaign_name}
                  />
                </Form.Group>
                <span className="text-danger">{errors["campaign_name"]}</span>
              </Col>
              <Col
                md={5}
                className="entity-picker-create-field entity-picker-create-field-wide"
              >
                <Form.Group controlId="comment">
                  <Form.Control
                    name="comment"
                    size="sm"
                    placeholder={t("COMMENT")}
                    onChange={(v: any) =>
                      handleFormChange({ path: "comment", value: v })
                    }
                    value={formSaveData?.comment}
                  />
                </Form.Group>
                <span className="text-danger">{errors["comment"]}</span>
              </Col>
              <Col md={2} className="entity-picker-create-actions">
                <div className="entity-picker-create-buttons">
                  <Button title={t("ADD")} onClick={addCampaign}>
                    <SaveIcon />
                  </Button>
                  <Button title={t("CLEAR")} onClick={clearSaveForm}>
                    <CloseIcon />
                  </Button>
                </div>
              </Col>
            </Form>
          )}
          <div className="row entity-picker-toolbar-row campaign-controls-row">
            <div className="col-md-4 entity-picker-select-col">
              {onSelectCampaign && (
                <Button
                  className="button entity-picker-select-btn"
                  appearance="primary"
                  onClick={() => setSelectedCampaign()}
                >
                  {t("SELECT") + " " + t("CAMPAIGN")}
                </Button>
              )}
            </div>
            <div className="col-md-8 mb-2 text-right campaign-search-col">
              <InputGroup className="entity-picker-searchbar campaign-smart-search">
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("CAMPAIGN").toLowerCase() +
                      " " +
                      t("NAME").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterCampaigns(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterCampaigns(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>
          <div
            className={total > 0 ? "campaign-table" : "campaign-empty-wrap"}
          >
            <Table
              className="entity-picker-table"
              loading={loading}
              height={400}
              fillHeight={true}
              data={data}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              sortType={sort_order}
              bordered
              cellBordered
              hover={true}
              renderEmpty={() => (
                <div className="entity-picker-empty-state campaign-empty-state">
                  <div className="entity-picker-empty-icon">
                    {getCompanyId() !== null ? "0" : "?"}
                  </div>
                  <h4 className="entity-picker-empty-title">
                    {getCompanyId() !== null
                      ? t("NO_ENTITY_FOUND", { entity: t("CAMPAIGN") })
                      : t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                  </h4>
                  <p className="entity-picker-empty-text">
                    {getCompanyId() !== null
                      ? t("ADJUST_FILTER") +
                        " " +
                        t("OR") +
                        " " +
                        t("CREATE_NEW_ENTITY", { entity: t("CAMPAIGN") })
                      : t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                  </p>
                </div>
              )}
            >
          <Column width={40} align="center" fixed>
            <CustomHeaderCell>
              <div>
                {multiSelect && (
                  <Checkbox
                    inline
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={handleCheckAll}
                  />
                )}
              </div>
            </CustomHeaderCell>
            <CheckCell
              dataKey="id"
              checkedKeys={checkedKeys}
              onChange={handleCheck}
            />
          </Column>
          <Column flexGrow={1} align="left" fixed sortable>
            <CustomHeaderCell>
              {t("CAMPAIGN") + " " + t("NAME")}
            </CustomHeaderCell>
            <MasterEditCell
              dataKey="campaign_name"
              onChange={handleDataChanges}
            />
          </Column>
          <Column flexGrow={1} align="left">
            <CustomHeaderCell>{t("COMMENT")}</CustomHeaderCell>
            <MasterEditCell dataKey="comment" onChange={handleDataChanges} />
          </Column>
          <Column flexGrow={1} align="center" sortable>
            <CustomHeaderCell>{t("STATUS")}</CustomHeaderCell>
            <MasterEditCell dataKey="status" onChange={handleDataChanges} />
          </Column>

          {showCreate ? (
            <Column flexGrow={1} align="center">
              <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
              <ActionCell
                dataKey="id"
                onClick={handleEditData}
                onDelete={(id: any) => {
                  setOpenDeleteModal(true);
                  setCampaignId(id);
                }}
              />
            </Column>
          ) : (
            ""
          )}
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            total={total}
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
      </Container>
      <AlertModal
        open={openNoCampaignModal}
        handleClose={(e: any) => {
          if (e) {
            setOpenNoCampaignModal(false);
          }
        }}
        displayText={t("PLEASE_SELECT") + " " + t("CAMPAIGN")}
        modalTitle={t("PLEASE_SELECT")}
        cancelButton={false}
      />
      <AlertModal
        open={openDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            handleDataDelete(campaignId);
            setOpenDeleteModal(false);
          } else {
            setOpenDeleteModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("CAMPAIGN").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("CAMPAIGN").toLowerCase(),
        })}
      />
    </div>
  );
}
export default Campaigns;
